export class SessionStorageExtension {
  // Set value in session storage
  static set(key: string, value: string | number) {
    try {
      const valueInStorage = window.sessionStorage.getItem(key);
      if (valueInStorage) {
        window.sessionStorage.removeItem(key);
      }
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Silently ignore: read-only filesystem state (private browsing, full storage)
    }
  }

  // Get value from session storage
  static get(key: string) {
    const storedItemJSON = window.sessionStorage.getItem(key);
    if (!storedItemJSON) return null;
    try {
      return JSON.parse(storedItemJSON);
    } catch (error) {
      return storedItemJSON;
    }
  }

  // Remove value from session storage
  static remove(key: string) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Silently ignore
    }
  }
}

export class LocalStorageExtension {
  // Set value in local storage
  static set(key: string, value: string | number) {
    try {
      const valueInStorage = window.localStorage.getItem(key);
      if (valueInStorage) {
        window.localStorage.removeItem(key);
      }
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Silently ignore: read-only filesystem state (private browsing, full storage)
    }
  }

  // Get value from local storage
  static get(key: string) {
    const storedItemJSON = window.localStorage.getItem(key);
    if (!storedItemJSON) return null;
    try {
      return JSON.parse(storedItemJSON);
    } catch (error) {
      return storedItemJSON;
    }
  }

  // Remove value from local storage
  static remove(key: string) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Silently ignore
    }
  }
}

export class CookieStorageExtension {
  // Check if we're on localhost (HTTP) - secure cookies don't work on HTTP
  private static isLocalhost(): boolean {
    return (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.protocol === 'http:')
    );
  }

  // Set value in cookies
  static set(
    key: string,
    value: string | number,
    options: {
      days?: number;
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
  ) {
    // Auto-detect secure based on environment - localhost uses HTTP so secure must be false
    const isLocal = this.isLocalhost();
    const {
      days = 7,
      path = '/',
      domain,
      secure = !isLocal,
      sameSite = 'Lax'
    } = options;

    let cookieString = `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  }

  // Get value from cookies
  static get(key: string): string | null {
    const nameEQ = encodeURIComponent(key) + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(
          cookie.substring(nameEQ.length, cookie.length)
        );
        try {
          return JSON.parse(value);
        } catch (error) {
          return value;
        }
      }
    }
    return null;
  }

  // Remove value from cookies
  // Note: Safari requires matching attributes (secure, sameSite) for proper cookie deletion
  static remove(
    key: string,
    options: {
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
  ) {
    // Auto-detect secure based on environment to match how cookie was set
    const isLocal = this.isLocalhost();
    const { path = '/', domain, secure = !isLocal, sameSite = 'Lax' } = options;

    let cookieString = `${encodeURIComponent(key)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  }
}
